import type { triggerToastNotification } from "@dfa/ui-platform";
import { triggerToast } from "components/toasts/triggerToast";
import { useEffect, useState } from "react";

type MountToastParams<P = unknown> = {
	component: (props: P) => React.ReactElement;
	componentProps: P;
	triggerSettings?: Parameters<typeof triggerToastNotification>[2];
} & {
	trigger: "timeSpent" | "inactivity" | "pageLoad" | "scrollDepth";
	distance?: number;
	duration?: number;
};

const useMountToast = <P = Parameters<typeof triggerToastNotification>[1],>({
	component,
	componentProps,
	triggerSettings,
	trigger,
	duration,
	distance
}: MountToastParams<P>) => {
	const [toastHasFired, setToastHasFired] = useState<boolean>(false);

	if (duration) duration *= 1000; // Value will come in as seconds. Updates to milliseconds

	useEffect(() => {
		if (toastHasFired || !component) return;

		let timeoutId: NodeJS.Timeout;

		const fireToast = () => {
			setToastHasFired(true);
			clearTimeout(timeoutId);
			triggerToast(component, componentProps, triggerSettings);
		};

		if (trigger === "timeSpent") {
			timeoutId = setTimeout(fireToast, duration);
			return () => clearTimeout(timeoutId);
		} else if (trigger === "inactivity") {
			const resetTimeout = () => {
				clearTimeout(timeoutId);
				timeoutId = setTimeout(fireToast, duration);
			};

			const activityEvents = [
				"mousemove",
				"mousedown",
				"touchstart",
				"touchmove",
				"scroll",
				"click",
				"keydown"
			];

			activityEvents.forEach((activityEvent) =>
				window.addEventListener(activityEvent, resetTimeout, true)
			);

			resetTimeout();
			return () => {
				activityEvents.forEach((activityEvent) =>
					window.removeEventListener(activityEvent, resetTimeout, true)
				);
				clearTimeout(timeoutId);
			};
		} else if (trigger === "pageLoad") {
			fireToast();
		} else if (trigger === "scrollDepth") {
			let hasHitGoal = false;
			const detectScrollPosition = () => {
				if (hasHitGoal) return;

				const scrollTop = window.scrollY;
				const totalScrollableHeight = document.documentElement.scrollHeight;
				const triggerPoint = (totalScrollableHeight - window.innerHeight) * (distance || 0.5);

				if (scrollTop >= triggerPoint) {
					hasHitGoal = true;
					window.removeEventListener("scroll", detectScrollPosition);

					if (duration) {
						timeoutId = setTimeout(fireToast, duration);
					} else {
						fireToast();
					}
				}
			};

			window.addEventListener("scroll", detectScrollPosition, true);

			return () => {
				window.removeEventListener("scroll", detectScrollPosition);
				clearTimeout(timeoutId);
			};
		}

		return;
	}, [component, componentProps, triggerSettings, trigger, toastHasFired, duration, distance]);
};

export default useMountToast;
